import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import GenericButton from "../../components/Custom/GenericButton";
import api from "../../globals/axios";
import { useAlert } from "../../components/Alert";
import { useTranslation } from "react-i18next";
import AdminDashboardCard from "../../components/Custom/AdminDashboardCard";
import CustomAppBar from "../../components/AppBar";
import { useNavigate } from "react-router-dom";
import { getAuth } from "../../globals/authentication";

const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
    minHeight: "100vh",
    marginBottom: "-4rem",
  },
  container: {
    padding: "20px",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  title: {
    textAlign: "center",
    padding: "20px 0",
    borderRadius: "4px",
    marginBottom: "20px",
    border: "1px solid rgba(0, 0, 0, 0.12)",
  },
  statsContainer: {
    display: "flex",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "24px",
    flexWrap: "wrap",
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    padding: "20px 0",
  },
  filterContainer: {
    display: "flex",
    gap: "16px",
    marginBottom: "20px",
    flexWrap: "wrap",
  },
}));

export default function AdminDashboard() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [farmers, setFarmers] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    total_pages: 0,
    total_objects: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPopFilter, setShowPopFilter] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    area_mapped: "",
    name: "",
    vle: "",
    area_manager: "",
    pop_manager: "",
    village: "",
    phone_number: "",
  });

  // Stats items
  const statsItems = [
    {
      label: t("Pop Managers"),
      value: stats.total_pop_managers || 0,
      color: "#FFEB3B",
    },
    {
      label: t("Area Managers"),
      value: stats.total_area_managers || 0,
      color: "#FF5722",
    },
    {
      label: t("Village level Entrepreneurs (VLEs)"),
      value: stats.total_vles || 0,
      color: "#00BCD4",
    },
    {
      label: t("Farmers"),
      value: stats.total_farmers || 0,
      color: "#8BC34A",
    },
    {
      label: t("Area Mapped (in acres)"),
      value: stats.total_farm_area || 0,
      color: "#FFC107",
    },
  ];

  // Define table columns
  const columns = [
    { key: "farmer_name", label: t("Farmer Name") },
    { key: "mobile", label: t("Mobile") },
    { key: "email", label: t("Email") },
    { key: "area", label: t("Area Mapped (acres)") },
    { key: "farms_count", label: t("No.of Farms") },
    { key: "crops", label: t("Crops") },
    { key: "vle", label: t("VLE") },
    { key: "area_manager", label: t("Area Manager") },
    { key: "pop_manager", label: t("POP Manager") },
    { key: "village", label: t("Village") },
  ];

  const authDetails = getAuth();

  // Fetch stats
  const fetchStats = () => {
    const { role } = authDetails || {};
    let url = "/app-admin/get-farming-stats/";
    if (role === "CA") {
      url = "/app-admin/get-ca-farming-stats/";
    }
    api
      .get(url)
      .then((res) => {
        if (res && res.data) {
          setStats(res.data.data);
        }
      })
      .catch((err) => {
        console.error("Error fetching stats:", err);
        showAlert("error", "Couldn't fetch the stats. Please try again later!");
      });
  };

  // Fetch farmers data
  const fetchFarmersData = (page = 1) => {
    setIsLoading(true);
    let {
      area_mapped,
      name,
      vle,
      area_manager,
      pop_manager,
      village,
      phone_number,
    } = filters;
    let query = `?page=${page}`;
    if (area_mapped) query += `&area_mapped=${area_mapped}`;
    if (name) query += `&name=${name}`;
    if (vle) query += `&vle=${vle}`;
    if (area_manager) query += `&area_manager=${area_manager}`;
    if (pop_manager) query += `&pop_manager=${pop_manager}`;
    if (village) query += `&village=${village}`;
    if (phone_number) query += `&phone_number=${phone_number}`;

    const { role } = authDetails || {};
    let url = `/app-admin/get-admin-farmer-details/${query}`;
    if (role === "CA") {
      url = `/app-admin/get-ca-farmer-details/${query}`;
    }
    api
      .get(url)
      .then((res) => {
        if (res?.data) {
          setFarmers(res.data.results);
          setPagination({
            page: res.data.current_page_number,
            total_pages: res.data.total_pages,
            total_objects: res.data.total_objects,
          });
        }
        setError("");
      })
      .catch((err) => {
        console.error("Error fetching farmers:", err);
        setError("Couldn't fetch the farmers data. Please try again later!");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    fetchFarmersData(value);
  };

  // Initial data fetch
  useEffect(() => {
    const { role } = authDetails || {};
    if (role === "CA") {
      setShowPopFilter(true);
    }
    fetchStats();
    fetchFarmersData();
  }, []);

  // handle detailed farmer view
  const handleDetailedFarmerView = (farmerId) => {
    navigate(`/farmer-details/${farmerId}`);
  };

  if (
    authDetails &&
    !(authDetails.role == "ADMIN" || authDetails.role == "CA")
  ) {
    navigate("/login");
  }

  return (
    <Box className={classes.root} component={Paper}>
      <Helmet>
        <title>Admin Dashboard - Farmapp</title>
      </Helmet>
      <CustomAppBar />

      <Box className={classes.container}>
        <Paper className={classes.title} elevation={0}>
          <Typography variant="h5">{t("Admin Dashboard")}</Typography>
        </Paper>

        <Box className={classes.statsContainer}>
          {statsItems.map((stat, index) => (
            <AdminDashboardCard
              key={index}
              label={stat.label}
              value={stat.value}
              color={stat.color}
            />
          ))}
        </Box>

        <Box className={classes.filterContainer}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth>
                <InputLabel id="area-mapped-label">
                  {t("Area Mapped")}
                </InputLabel>
                <Select
                  labelId="area-mapped-label"
                  value={filters.area_mapped}
                  label={t("Area Mapped")}
                  onChange={(e) =>
                    setFilters({ ...filters, area_mapped: e.target.value })
                  }
                >
                  <MenuItem value="">{t("All")}</MenuItem>
                  <MenuItem value="yes">{t("Yes")}</MenuItem>
                  <MenuItem value="no">{t("No")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label={t("Farmer Name")}
                value={filters.name}
                onChange={(e) =>
                  setFilters({ ...filters, name: e.target.value })
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label={t("Village")}
                value={filters.village}
                onChange={(e) =>
                  setFilters({ ...filters, village: e.target.value })
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label={t("VLE")}
                value={filters.vle}
                onChange={(e) =>
                  setFilters({ ...filters, vle: e.target.value })
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label={t("Area Manager")}
                value={filters.area_manager}
                onChange={(e) =>
                  setFilters({ ...filters, area_manager: e.target.value })
                }
                fullWidth
              />
            </Grid>

            {showPopFilter && (
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  label={t("Pop Manager")}
                  value={filters.pop_manager}
                  onChange={(e) =>
                    setFilters({ ...filters, pop_manager: e.target.value })
                  }
                  fullWidth
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label={t("Mobile Number")}
                value={filters.phone_number}
                onChange={(e) =>
                  setFilters({ ...filters, phone_number: e.target.value })
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <GenericButton
                backgroundColor={"#4CAF50"}
                fontColor={"#fff"}
                fontSize={"14px"}
                fullWidth
                padding={"15px 10px"}
                onClick={() => fetchFarmersData()}
              >
                {t("Search")}
              </GenericButton>
            </Grid>
          </Grid>
        </Box>

        <TableContainer component={Paper}>
          <Table sx={{ borderCollapse: "collapse" }}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    align="center"
                    sx={{
                      borderRight: "1px solid rgba(224, 224, 224, 1)",
                      fontWeight: "bold",
                      backgroundColor: "#FFC107",
                      color: "white",
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "#FFC107",
                    color: "white",
                    borderRight: "1px solid rgba(224, 224, 224, 1)",
                  }}
                >
                  {t("Action")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    {t("Loading")}...
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    {error}
                  </TableCell>
                </TableRow>
              ) : (
                farmers.map((farmer, index) => (
                  <TableRow key={index}>
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        align="center"
                        sx={{
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                        }}
                      >
                        {farmer[column.key] || "-"}
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <GenericButton
                        backgroundColor={"#4CAF50"}
                        fontColor={"#fff"}
                        fontSize={"12px"}
                        padding={"5px 10px"}
                        onClick={() =>
                          handleDetailedFarmerView(farmer.farmer_id)
                        }
                      >
                        {t("View")}
                      </GenericButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box className={classes.pagination}>
          <Pagination
            count={pagination.total_pages}
            page={pagination.page}
            onChange={handlePageChange}
            color="primary"
            disabled={isLoading}
          />
        </Box>
      </Box>
    </Box>
  );
}
